import { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard,
  Building2,
  Plug,
  Users,
  ScrollText,
  Settings,
  Cpu,
  MemoryStick,
  Timer,
  Clock,
  Wifi,
  Activity,
  Zap,
  RefreshCw,
  Trash2,
  HeartPulse,
  CheckCircle,
  AlertTriangle,
  Search,
  Plus,
  Eye,
  Ban,
  UserPlus,
  Pencil,
  UserX,
  Play,
  ToggleLeft,
  ToggleRight,
  Save,
  Shield,
  Info,
  AlertOctagon,
  Bug,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AdminTab = 'overview' | 'tenants' | 'connectors' | 'users' | 'logs' | 'settings';

interface Tenant {
  id: string;
  name: string;
  plan: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'suspended' | 'trial';
  created: string;
  userCount: number;
  connectorCount: number;
}

interface ConnectorInstance {
  id: string;
  type: string;
  tenant: string;
  status: 'healthy' | 'degraded' | 'down';
  lastCheck: string;
  responseTime: number;
  errorCount24h: number;
}

interface AdminUser {
  id: string;
  email: string;
  role: 'super_admin' | 'admin' | 'viewer';
  lastLogin: string;
  status: 'active' | 'inactive';
}

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  source: string;
}

interface AdminSettings {
  maintenanceMode: boolean;
  debugLogging: boolean;
  rateLimitingEnabled: boolean;
  selfDemotionEnabled: boolean;
  maxTenants: number;
  defaultPlan: string;
  apiRateLimit: number;
  sessionTimeout: number;
}

// ---------------------------------------------------------------------------
// Mock Data
// ---------------------------------------------------------------------------

const MOCK_TENANTS: Tenant[] = [
  {
    id: 't1',
    name: 'Acme Corp',
    plan: 'enterprise',
    status: 'active',
    created: '2025-06-15',
    userCount: 34,
    connectorCount: 8,
  },
  {
    id: 't2',
    name: 'Globex Industries',
    plan: 'pro',
    status: 'active',
    created: '2025-08-22',
    userCount: 12,
    connectorCount: 4,
  },
  {
    id: 't3',
    name: 'Initech',
    plan: 'pro',
    status: 'suspended',
    created: '2025-03-10',
    userCount: 8,
    connectorCount: 3,
  },
  {
    id: 't4',
    name: 'Hooli',
    plan: 'enterprise',
    status: 'active',
    created: '2025-01-05',
    userCount: 56,
    connectorCount: 12,
  },
  {
    id: 't5',
    name: 'Stark Security',
    plan: 'free',
    status: 'trial',
    created: '2026-01-28',
    userCount: 2,
    connectorCount: 1,
  },
  {
    id: 't6',
    name: 'Cyberdyne Systems',
    plan: 'pro',
    status: 'active',
    created: '2025-11-11',
    userCount: 18,
    connectorCount: 5,
  },
  {
    id: 't7',
    name: 'Wayne Enterprises',
    plan: 'enterprise',
    status: 'active',
    created: '2025-04-01',
    userCount: 72,
    connectorCount: 15,
  },
];

const MOCK_CONNECTORS: ConnectorInstance[] = [
  {
    id: 'c1',
    type: 'CrowdStrike',
    tenant: 'Acme Corp',
    status: 'healthy',
    lastCheck: '2026-02-16T14:32:00Z',
    responseTime: 120,
    errorCount24h: 0,
  },
  {
    id: 'c2',
    type: 'Splunk',
    tenant: 'Acme Corp',
    status: 'healthy',
    lastCheck: '2026-02-16T14:31:55Z',
    responseTime: 340,
    errorCount24h: 1,
  },
  {
    id: 'c3',
    type: 'Okta',
    tenant: 'Globex Industries',
    status: 'degraded',
    lastCheck: '2026-02-16T14:30:10Z',
    responseTime: 1850,
    errorCount24h: 14,
  },
  {
    id: 'c4',
    type: 'AWS GuardDuty',
    tenant: 'Hooli',
    status: 'healthy',
    lastCheck: '2026-02-16T14:32:05Z',
    responseTime: 95,
    errorCount24h: 0,
  },
  {
    id: 'c5',
    type: 'Microsoft Sentinel',
    tenant: 'Hooli',
    status: 'healthy',
    lastCheck: '2026-02-16T14:31:48Z',
    responseTime: 210,
    errorCount24h: 2,
  },
  {
    id: 'c6',
    type: 'Palo Alto Cortex',
    tenant: 'Wayne Enterprises',
    status: 'down',
    lastCheck: '2026-02-16T13:15:00Z',
    responseTime: 0,
    errorCount24h: 87,
  },
  {
    id: 'c7',
    type: 'Splunk',
    tenant: 'Cyberdyne Systems',
    status: 'healthy',
    lastCheck: '2026-02-16T14:32:01Z',
    responseTime: 280,
    errorCount24h: 0,
  },
  {
    id: 'c8',
    type: 'Okta',
    tenant: 'Acme Corp',
    status: 'healthy',
    lastCheck: '2026-02-16T14:31:50Z',
    responseTime: 150,
    errorCount24h: 0,
  },
  {
    id: 'c9',
    type: 'GCP Security Command',
    tenant: 'Wayne Enterprises',
    status: 'healthy',
    lastCheck: '2026-02-16T14:32:03Z',
    responseTime: 175,
    errorCount24h: 1,
  },
  {
    id: 'c10',
    type: 'CrowdStrike',
    tenant: 'Stark Security',
    status: 'degraded',
    lastCheck: '2026-02-16T14:28:00Z',
    responseTime: 2400,
    errorCount24h: 9,
  },
];

const MOCK_USERS: AdminUser[] = [
  {
    id: 'u1',
    email: 'ibrahim@zoroagent.com',
    role: 'super_admin',
    lastLogin: '2026-02-16T14:00:00Z',
    status: 'active',
  },
  {
    id: 'u2',
    email: 'sarah.chen@zoroagent.com',
    role: 'admin',
    lastLogin: '2026-02-16T10:30:00Z',
    status: 'active',
  },
  {
    id: 'u3',
    email: 'marcus.riley@zoroagent.com',
    role: 'admin',
    lastLogin: '2026-02-15T18:45:00Z',
    status: 'active',
  },
  {
    id: 'u4',
    email: 'ai-ops@zoroagent.com',
    role: 'viewer',
    lastLogin: '2026-02-14T09:00:00Z',
    status: 'active',
  },
  {
    id: 'u5',
    email: 'former.admin@zoroagent.com',
    role: 'admin',
    lastLogin: '2026-01-20T12:00:00Z',
    status: 'inactive',
  },
];

const MOCK_LOGS: LogEntry[] = [
  {
    id: 'l1',
    timestamp: '2026-02-16T14:32:10Z',
    level: 'info',
    message: 'Health check completed successfully for all connectors',
    source: 'ConnectorManager',
  },
  {
    id: 'l2',
    timestamp: '2026-02-16T14:31:55Z',
    level: 'warn',
    message:
      'Okta connector for Globex Industries response time exceeds threshold (1850ms > 1000ms)',
    source: 'ConnectorManager',
  },
  {
    id: 'l3',
    timestamp: '2026-02-16T14:30:00Z',
    level: 'info',
    message: 'Tenant "Stark Security" trial period started (14 days remaining)',
    source: 'TenantService',
  },
  {
    id: 'l4',
    timestamp: '2026-02-16T14:28:45Z',
    level: 'error',
    message: 'Palo Alto Cortex connector for Wayne Enterprises - connection refused',
    source: 'ConnectorManager',
  },
  {
    id: 'l5',
    timestamp: '2026-02-16T14:25:00Z',
    level: 'info',
    message: 'User sarah.chen@zoroagent.com authenticated via SSO',
    source: 'AuthService',
  },
  {
    id: 'l6',
    timestamp: '2026-02-16T14:20:30Z',
    level: 'debug',
    message: 'Cache invalidated for tenant config: Acme Corp',
    source: 'CacheService',
  },
  {
    id: 'l7',
    timestamp: '2026-02-16T14:18:00Z',
    level: 'info',
    message: 'Rate limit policy updated: 1000 req/min -> 1200 req/min',
    source: 'RateLimiter',
  },
  {
    id: 'l8',
    timestamp: '2026-02-16T14:15:22Z',
    level: 'warn',
    message: 'API rate limit approaching threshold for tenant Hooli (890/1000 req/min)',
    source: 'RateLimiter',
  },
  {
    id: 'l9',
    timestamp: '2026-02-16T14:10:00Z',
    level: 'info',
    message: 'Autonomous agent investigation completed: suspicious login cluster for Acme Corp',
    source: 'AgentCore',
  },
  {
    id: 'l10',
    timestamp: '2026-02-16T14:05:15Z',
    level: 'error',
    message:
      'Failed to rotate API key for CrowdStrike connector (Stark Security) - insufficient permissions',
    source: 'KeyRotation',
  },
  {
    id: 'l11',
    timestamp: '2026-02-16T14:00:00Z',
    level: 'info',
    message: 'Scheduled maintenance window check - no maintenance scheduled',
    source: 'Scheduler',
  },
  {
    id: 'l12',
    timestamp: '2026-02-16T13:55:30Z',
    level: 'debug',
    message: 'WebSocket connections: 23 active, 2 idle, 0 errored',
    source: 'WSManager',
  },
  {
    id: 'l13',
    timestamp: '2026-02-16T13:50:00Z',
    level: 'info',
    message: 'Tenant Cyberdyne Systems upgraded from free to pro plan',
    source: 'BillingService',
  },
  {
    id: 'l14',
    timestamp: '2026-02-16T13:45:12Z',
    level: 'warn',
    message: 'Disk usage on worker-03 at 82% - approaching threshold',
    source: 'InfraMonitor',
  },
  {
    id: 'l15',
    timestamp: '2026-02-16T13:40:00Z',
    level: 'info',
    message: 'Daily compliance report generated for 7 active tenants',
    source: 'ComplianceEngine',
  },
  {
    id: 'l16',
    timestamp: '2026-02-16T13:35:00Z',
    level: 'error',
    message: 'Splunk HEC token expired for Initech - connector suspended',
    source: 'ConnectorManager',
  },
  {
    id: 'l17',
    timestamp: '2026-02-16T13:30:00Z',
    level: 'debug',
    message: 'Agent task queue: 3 pending, 1 executing, 12 completed (last hour)',
    source: 'TaskScheduler',
  },
  {
    id: 'l18',
    timestamp: '2026-02-16T13:25:00Z',
    level: 'info',
    message: 'New connector registered: GCP Security Command Center for Wayne Enterprises',
    source: 'ConnectorManager',
  },
  {
    id: 'l19',
    timestamp: '2026-02-16T13:20:15Z',
    level: 'warn',
    message: 'Memory usage spike detected on API gateway (4.2GB / 8GB)',
    source: 'InfraMonitor',
  },
  {
    id: 'l20',
    timestamp: '2026-02-16T13:15:00Z',
    level: 'info',
    message: 'System backup completed successfully (2.3GB, 4m 12s)',
    source: 'BackupService',
  },
];

const DEFAULT_SETTINGS: AdminSettings = {
  maintenanceMode: false,
  debugLogging: false,
  rateLimitingEnabled: true,
  selfDemotionEnabled: false,
  maxTenants: 50,
  defaultPlan: 'free',
  apiRateLimit: 1000,
  sessionTimeout: 30,
};

// ---------------------------------------------------------------------------
// Tab configuration
// ---------------------------------------------------------------------------

const ADMIN_TABS: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'System Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
  { id: 'tenants', label: 'Tenants', icon: <Building2 className="w-4 h-4" /> },
  { id: 'connectors', label: 'Connector Health', icon: <Plug className="w-4 h-4" /> },
  { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
  { id: 'logs', label: 'System Logs', icon: <ScrollText className="w-4 h-4" /> },
  { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
];

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, string> = {
    active: 'badge badge-success',
    healthy: 'badge badge-success',
    running: 'badge badge-success',
    trial: 'badge badge-warning',
    degraded: 'badge badge-warning',
    paused: 'badge badge-warning',
    suspended: 'badge badge-danger',
    down: 'badge badge-danger',
    error: 'badge badge-danger',
    inactive: 'badge bg-slate-500/20 text-slate-400 border border-slate-500/30',
  };
  return (
    <span
      className={
        config[status] || 'badge bg-slate-500/20 text-slate-400 border border-slate-500/30'
      }
    >
      {status}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const config: Record<string, string> = {
    free: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
    pro: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    enterprise: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
  };
  return <span className={`badge ${config[plan] || ''}`}>{plan}</span>;
}

function RoleBadge({ role }: { role: string }) {
  const config: Record<string, string> = {
    super_admin: 'bg-red-500/20 text-red-400 border border-red-500/30',
    admin: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    viewer: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
  };
  return <span className={`badge ${config[role] || ''}`}>{role.replace('_', ' ')}</span>;
}

function LevelBadge({ level }: { level: string }) {
  const config: Record<string, { cls: string; icon: React.ReactNode }> = {
    info: {
      cls: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
      icon: <Info className="w-3 h-3" />,
    },
    warn: {
      cls: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      icon: <AlertTriangle className="w-3 h-3" />,
    },
    error: {
      cls: 'bg-red-500/20 text-red-400 border border-red-500/30',
      icon: <AlertOctagon className="w-3 h-3" />,
    },
    debug: {
      cls: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
      icon: <Bug className="w-3 h-3" />,
    },
  };
  const c = config[level] || config.info;
  return (
    <span className={`badge flex items-center gap-1 ${c.cls}`}>
      {c.icon}
      {level}
    </span>
  );
}

function MetricCard({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-slate-400 mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color || 'text-white'}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function ToggleSwitch({
  enabled,
  onToggle,
  label,
}: {
  enabled: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center justify-between w-full p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-colors"
    >
      <span className="text-sm text-slate-200">{label}</span>
      {enabled ? (
        <ToggleRight className="w-6 h-6 text-primary-400" />
      ) : (
        <ToggleLeft className="w-6 h-6 text-slate-500" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section Components
// ---------------------------------------------------------------------------

function SystemOverview() {
  const [agentStatus] = useState<'running' | 'paused' | 'error'>('running');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Health Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={<Cpu className="w-4 h-4" />}
          label="CPU Usage"
          value="23%"
          sub="4 cores"
          color="text-green-400"
        />
        <MetricCard
          icon={<MemoryStick className="w-4 h-4" />}
          label="Memory"
          value="4.1 GB"
          sub="of 8 GB (51%)"
          color="text-blue-400"
        />
        <MetricCard
          icon={<Timer className="w-4 h-4" />}
          label="API Latency"
          value="42ms"
          sub="p99: 187ms"
          color="text-primary-400"
        />
        <MetricCard
          icon={<Clock className="w-4 h-4" />}
          label="Uptime"
          value="14d 7h"
          sub="Since Feb 2"
          color="text-emerald-400"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={<Wifi className="w-4 h-4" />} label="Active Connections" value="23" />
        <MetricCard
          icon={<Activity className="w-4 h-4" />}
          label="Requests / min"
          value="847"
          sub="avg last 5 min"
        />
        <MetricCard
          icon={<Zap className="w-4 h-4" />}
          label="Agent Status"
          value={agentStatus}
          color={
            agentStatus === 'running'
              ? 'text-green-400'
              : agentStatus === 'paused'
                ? 'text-yellow-400'
                : 'text-red-400'
          }
        />
        <MetricCard
          icon={<Shield className="w-4 h-4" />}
          label="Version"
          value="v1.4.2"
          sub="Deployed Feb 14, 09:30 UTC"
        />
      </div>

      {/* Quick Actions */}
      <div className="glass rounded-2xl p-6">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <button className="btn btn-primary flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" />
            Restart Agent
          </button>
          <button className="btn btn-ghost flex items-center gap-2 text-sm border border-slate-600/50">
            <Trash2 className="w-4 h-4" />
            Clear Cache
          </button>
          <button className="btn btn-ghost flex items-center gap-2 text-sm border border-slate-600/50">
            <HeartPulse className="w-4 h-4" />
            Force Health Check
          </button>
        </div>
      </div>
    </div>
  );
}

function TenantManagement() {
  const [tenants] = useState<Tenant[]>(MOCK_TENANTS);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState<string>('all');

  const filtered = tenants.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase());
    const matchesPlan = filterPlan === 'all' || t.plan === filterPlan;
    return matchesSearch && matchesPlan;
  });

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tenants..."
            className="input pl-10"
          />
        </div>
        <select
          value={filterPlan}
          onChange={e => setFilterPlan(e.target.value)}
          className="input w-auto"
        >
          <option value="all">All Plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <button className="btn btn-primary flex items-center gap-2 text-sm whitespace-nowrap">
          <Plus className="w-4 h-4" />
          Add Tenant
        </button>
      </div>

      {/* Table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Connectors
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr
                  key={t.id}
                  className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors"
                >
                  <td className="px-4 py-3 text-white font-medium">{t.name}</td>
                  <td className="px-4 py-3">
                    <PlanBadge plan={t.plan} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-400">{t.created}</td>
                  <td className="px-4 py-3 text-slate-300">{t.userCount}</td>
                  <td className="px-4 py-3 text-slate-300">{t.connectorCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        className="p-1.5 hover:bg-slate-700/50 rounded-lg text-slate-400 hover:text-white transition-colors"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-yellow-500/20 rounded-lg text-slate-400 hover:text-yellow-400 transition-colors"
                        title="Suspend"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No tenants match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ConnectorHealth() {
  const [connectors] = useState<ConnectorInstance[]>(MOCK_CONNECTORS);
  const [testing, setTesting] = useState(false);

  const handleTestAll = () => {
    setTesting(true);
    setTimeout(() => setTesting(false), 2000);
  };

  const getResponseColor = (ms: number) => {
    if (ms === 0) return 'text-slate-500';
    if (ms < 500) return 'text-green-400';
    if (ms < 1500) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20 text-center">
          <div className="text-2xl font-bold text-emerald-400">
            {connectors.filter(c => c.status === 'healthy').length}
          </div>
          <div className="text-xs text-slate-400">Healthy</div>
        </div>
        <div className="p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20 text-center">
          <div className="text-2xl font-bold text-yellow-400">
            {connectors.filter(c => c.status === 'degraded').length}
          </div>
          <div className="text-xs text-slate-400">Degraded</div>
        </div>
        <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20 text-center">
          <div className="text-2xl font-bold text-red-400">
            {connectors.filter(c => c.status === 'down').length}
          </div>
          <div className="text-xs text-slate-400">Down</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex justify-end">
        <button
          onClick={handleTestAll}
          disabled={testing}
          className="btn btn-primary flex items-center gap-2 text-sm"
        >
          {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {testing ? 'Testing...' : 'Test All'}
        </button>
      </div>

      {/* Table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Connector
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Last Check
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Response Time
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Errors (24h)
                </th>
              </tr>
            </thead>
            <tbody>
              {connectors.map(c => (
                <tr
                  key={c.id}
                  className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors"
                >
                  <td className="px-4 py-3 text-white font-medium flex items-center gap-2">
                    <Plug className="w-4 h-4 text-slate-500" />
                    {c.type}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{c.tenant}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {new Date(c.lastCheck).toLocaleTimeString()}
                  </td>
                  <td className={`px-4 py-3 font-mono text-xs ${getResponseColor(c.responseTime)}`}>
                    {c.responseTime === 0 ? '--' : `${c.responseTime}ms`}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`font-mono text-xs ${c.errorCount24h > 10 ? 'text-red-400' : c.errorCount24h > 0 ? 'text-yellow-400' : 'text-slate-500'}`}
                    >
                      {c.errorCount24h}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UserManagement() {
  const [users] = useState<AdminUser[]>(MOCK_USERS);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toolbar */}
      <div className="flex justify-end">
        <button className="btn btn-primary flex items-center gap-2 text-sm">
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr
                  key={u.id}
                  className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors"
                >
                  <td className="px-4 py-3 text-white font-medium">{u.email}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {new Date(u.lastLogin).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        className="p-1.5 hover:bg-blue-500/20 rounded-lg text-slate-400 hover:text-blue-400 transition-colors"
                        title="Edit Role"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400 transition-colors"
                        title="Deactivate"
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SystemLogs() {
  const [logs] = useState<LogEntry[]>(MOCK_LOGS);
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const filtered = logs.filter(l => levelFilter === 'all' || l.level === levelFilter);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [filtered, autoScroll]);

  const levelCounts = {
    info: logs.filter(l => l.level === 'info').length,
    warn: logs.filter(l => l.level === 'warn').length,
    error: logs.filter(l => l.level === 'error').length,
    debug: logs.filter(l => l.level === 'debug').length,
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'info', 'warn', 'error', 'debug'].map(level => (
            <button
              key={level}
              onClick={() => setLevelFilter(level)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                levelFilter === level
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              {level === 'all' ? 'All' : level.toUpperCase()}
              {level !== 'all' && (
                <span className="ml-1.5 text-slate-500">
                  {levelCounts[level as keyof typeof levelCounts]}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">Auto-scroll</span>
          <button onClick={() => setAutoScroll(!autoScroll)}>
            {autoScroll ? (
              <ToggleRight className="w-5 h-5 text-primary-400" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-slate-500" />
            )}
          </button>
        </div>
      </div>

      {/* Log Viewer */}
      <div className="glass rounded-2xl overflow-hidden">
        <div ref={scrollRef} className="max-h-[500px] overflow-y-auto divide-y divide-slate-700/30">
          {filtered.map(log => (
            <div
              key={log.id}
              className="px-4 py-3 hover:bg-slate-700/20 transition-colors flex items-start gap-3"
            >
              <span className="text-xs text-slate-500 font-mono whitespace-nowrap pt-0.5">
                {new Date(log.timestamp).toLocaleTimeString()}
              </span>
              <div className="flex-shrink-0 pt-0.5">
                <LevelBadge level={log.level} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200 break-words">{log.message}</p>
                <span className="text-xs text-slate-500 mt-0.5 block">{log.source}</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-500">
              No log entries match the selected filter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [settings, setSettings] = useState<AdminSettings>(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);

  const toggleSetting = (key: keyof AdminSettings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    setSaved(false);
  };

  const updateSetting = (key: keyof AdminSettings, value: string | number) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Toggles */}
      <div className="glass rounded-2xl p-6">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
          Feature Toggles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ToggleSwitch
            enabled={settings.maintenanceMode}
            onToggle={() => toggleSetting('maintenanceMode')}
            label="Maintenance Mode"
          />
          <ToggleSwitch
            enabled={settings.debugLogging}
            onToggle={() => toggleSetting('debugLogging')}
            label="Debug Logging"
          />
          <ToggleSwitch
            enabled={settings.rateLimitingEnabled}
            onToggle={() => toggleSetting('rateLimitingEnabled')}
            label="Rate Limiting Enabled"
          />
          <ToggleSwitch
            enabled={settings.selfDemotionEnabled}
            onToggle={() => toggleSetting('selfDemotionEnabled')}
            label="Self-Demotion Enabled"
          />
        </div>
      </div>

      {/* Input Fields */}
      <div className="glass rounded-2xl p-6">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
          Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Max Tenants</label>
            <input
              type="number"
              value={settings.maxTenants}
              onChange={e => updateSetting('maxTenants', parseInt(e.target.value) || 0)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Default Plan</label>
            <select
              value={settings.defaultPlan}
              onChange={e => updateSetting('defaultPlan', e.target.value)}
              className="input"
            >
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">API Rate Limit (req/min)</label>
            <input
              type="number"
              value={settings.apiRateLimit}
              onChange={e => updateSetting('apiRateLimit', parseInt(e.target.value) || 0)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Session Timeout (minutes)</label>
            <input
              type="number"
              value={settings.sessionTimeout}
              onChange={e => updateSetting('sessionTimeout', parseInt(e.target.value) || 0)}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="btn btn-primary flex items-center gap-2 text-sm">
          <Save className="w-4 h-4" />
          Save Settings
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-green-400 animate-fade-in">
            <CheckCircle className="w-4 h-4" />
            Settings saved successfully
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main AdminPanel
// ---------------------------------------------------------------------------

export function AdminPanel() {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <SystemOverview />;
      case 'tenants':
        return <TenantManagement />;
      case 'connectors':
        return <ConnectorHealth />;
      case 'users':
        return <UserManagement />;
      case 'logs':
        return <SystemLogs />;
      case 'settings':
        return <SettingsPanel />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-lg shadow-red-500/25">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Internal Admin Panel</h2>
            <p className="text-sm text-slate-400">System management and configuration</p>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-2 flex-wrap">
          {ADMIN_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 border border-transparent'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
}
